import React, { useCallback, useMemo, useState } from 'react'

import { ArrowBackIcon, WarningOutlineIcon } from '@toasttab/buffet-pui-icons'
import { gPlacesAPIKey } from 'config/index'

import {
  dummyAddressValue,
  normalizeGoogleAddress
} from 'src/public/components/online_ordering/addressUtils'

import { ModalCloseButton } from 'src/shared/components/common/modal/Modal'
import { t } from 'src/translations'

import Image from 'shared/components/common/Image'
import InlinePlacesAutocomplete from 'shared/components/common/location_search/InlinePlacesAutocomplete'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useCart } from 'public/components/online_ordering/CartContext'

import { ScreenProps } from './DeliveryModal'
import { useTracker } from 'src/lib/js/hooks/useTracker'
import {
  getInvalidTrackingSource,
  trackEnteredDeliveryAddress,
  trackInvalidDeliveryAddressEntered,
  trackManualAddressButtonClicked,
  trackRedirectedToManualAddress,
  trackValidDeliveryAddressEntered
} from 'src/public/components/default_template/online_ordering/dining_behavior_toggle/fulfillmentTrackingUtils'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { useDelivery } from 'public/components/online_ordering/DeliveryContext'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  getCountryAddressConfig,
  getMissingRequiredFieldErrors,
  normalizeGoogleAddress as normalizeGoogleAddressForIntl,
  formatDeliveryAddress
} from 'src/public/components/online_ordering/address_configs'
import { DeliveryInfoInput } from 'src/apollo/onlineOrdering'

const AddressSearchScreen = ({
  goToScreen,
  closeModal,
  deliveryAddress,
  inRangeAddresses,
  outOfRangeAddresses,
  allowBackNav,
  trackingSource,
  partialDeliveryAddress,
  previousSearchText
}: ScreenProps) => {
  const { selectedLocation, i18n } = useRestaurant()
  const { isAddressInRange } = useDelivery()
  const { cart } = useCart()
  const tracker = useTracker()
  const [loading, setLoading] = useState(false)
  const countryAddressConfig = getCountryAddressConfig(i18n?.country)
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  /**
   * validation error state only pertinent to individual screen render lifecycles.
   * this validation error reflects issues upstream from delivery distance validation
   * of an address and restaurant couple (i.e. google auto complete failures)
   */
  const [validationError, setValidationError] = useState<string>('')

  // Redirect to the manual screen with partial address
  const handleRedirectToManualScreen = useCallback(
    (normalizedAddress: DeliveryInfoInput | null) => {
      trackRedirectedToManualAddress(
        tracker,
        cart?.order?.selections?.length || 0,
        false,
        trackingSource
      )

      return goToScreen('manual-address', null, undefined, normalizedAddress)
    },
    [tracker, cart?.order?.selections?.length, trackingSource, goToScreen]
  )

  const handlePlaceSelected = useCallback(
    async (place: google.maps.places.PlaceResult) => {
      trackEnteredDeliveryAddress(
        tracker,
        cart?.order?.selections?.length || 0,
        false,
        trackingSource
      )

      const addressHasStreetNumber = place.address_components?.some(
        (component) => component.types.includes('street_number')
      )

      const normalizedAddress = intlAddressCountryLogicErrorHandling
        ? normalizeGoogleAddressForIntl(place, i18n?.country)
        : normalizeGoogleAddress(place)

      if (
        (!intlAddressCountryLogicErrorHandling ||
          countryAddressConfig.requiresStreetNumber) &&
        !addressHasStreetNumber
      ) {
        if (intlAddressCountryLogicErrorHandling)
          return handleRedirectToManualScreen(normalizedAddress)

        return setValidationError(
          t('address-search-screen.enter-valid-address-with-street-number')
        )
      }

      if (intlAddressCountryLogicErrorHandling) {
        const requiredFieldErrors = getMissingRequiredFieldErrors(
          normalizedAddress,
          i18n?.country
        )

        if (requiredFieldErrors.length > 0) {
          return handleRedirectToManualScreen(normalizedAddress)
        }
      } else if (
        normalizedAddress?.zipCode === dummyAddressValue &&
        (i18n?.country === 'IE' || i18n?.country === 'GB')
      ) {
        return setValidationError(
          t(
            `address-search-screen.enter-valid-address-with-${
              i18n.country === 'IE' ? 'eircode' : 'postcode'
            }`
          )
        )
      }

      if (
        (cart?.restaurant || selectedLocation?.externalId) &&
        normalizedAddress
      ) {
        try {
          setLoading(true)
          const inRange = await isAddressInRange(normalizedAddress)
          setLoading(false)
          if (inRange) {
            goToScreen('address-confirm', normalizedAddress)
            trackValidDeliveryAddressEntered(
              tracker,
              cart?.order?.selections?.length || 0,
              false,
              trackingSource
            )
          } else {
            trackInvalidDeliveryAddressEntered(
              tracker,
              cart?.order?.selections?.length || 0,
              false,
              trackingSource
            )
            goToScreen(
              'address-invalid',
              normalizedAddress,
              getInvalidTrackingSource(trackingSource)
            )
          }
        } catch (_) {
          setLoading(false)
        }
      } else {
        trackInvalidDeliveryAddressEntered(
          tracker,
          cart?.order?.selections?.length || 0,
          false,
          trackingSource
        )
        goToScreen(
          'address-invalid',
          null,
          getInvalidTrackingSource(trackingSource)
        )
      }
    },
    [
      tracker,
      cart?.order?.selections?.length,
      cart?.restaurant,
      selectedLocation.externalId,
      isAddressInRange,
      i18n?.country,
      goToScreen,
      trackingSource
    ]
  )

  const defaultInput = useMemo(() => {
    if (deliveryAddress) {
      return formatDeliveryAddress(
        deliveryAddress,
        { full: true },
        intlAddressCountryLogicErrorHandling
      )
    }
    // Use previous search text for "no results" state preservation
    if (intlAddressCountryLogicErrorHandling && previousSearchText) {
      return previousSearchText
    }
    if (intlAddressCountryLogicErrorHandling && partialDeliveryAddress) {
      return formatDeliveryAddress(
        partialDeliveryAddress as Parameters<typeof formatDeliveryAddress>[0],
        { full: true },
        true
      )
    }
    return ''
  }, [
    deliveryAddress,
    intlAddressCountryLogicErrorHandling,
    previousSearchText,
    partialDeliveryAddress
  ])

  // offer back button if initial screen is not address-confirmation
  // and user is eligible with saved addresses
  const userCanGoBack =
    allowBackNav &&
    (inRangeAddresses.length > 0 || outOfRangeAddresses.length > 0)

  // if we have a tentative delivery address, keep in scope during transition back
  const backNavHandler = useCallback(() => {
    if (deliveryAddress) {
      goToScreen('address-hub')
    } else {
      goToScreen('address-hub', null)
    }
  }, [])

  return (
    <div data-testid='address-search-screen' className='addressSearchScreen'>
      <div className='modalHeader'>
        {userCanGoBack && (
          <button
            data-testid='address-search-back-btn'
            aria-label={t('fulfillment-general.back')}
            type='button'
            className='backArrow'
            onClick={backNavHandler}
          >
            <ArrowBackIcon />
          </button>
        )}
        <h2 id='delivery-address-modal-title' tabIndex={-1}>
          {t('address-search-screen.title')}
        </h2>
        <ModalCloseButton onClose={closeModal} />
      </div>
      {validationError && (
        <div className='warningBanner' data-testid='address-search-error'>
          <WarningOutlineIcon size='xs' accessibility='decorative' />
          <span>{validationError}</span>
        </div>
      )}
      <div className='modalContent'>
        <div className='addressInputContainer'>
          <InlinePlacesAutocomplete
            id='delivery-address-input-autocomplete'
            defaultValue={defaultInput}
            placeholder={t('fulfillment-general.enter-delivery-address')}
            helperText={t('inline-places-autocomplete.valid-delivery')}
            apiKey={gPlacesAPIKey}
            requireZip={
              intlAddressCountryLogicErrorHandling
                ? countryAddressConfig.fields.postalCode.required
                : true
            }
            onValueChange={() => {
              if (validationError) setValidationError('')
            }}
            onError={() => {
              return setValidationError(
                t('address-search-screen.trouble-verifying-address')
              )
            }}
            onPlaceSelected={handlePlaceSelected}
            triggerManualAddress={(searchText) => {
              trackManualAddressButtonClicked(
                tracker,
                cart?.order?.selections?.length || 0,
                false,
                trackingSource
              )

              goToScreen(
                'manual-address',
                undefined,
                undefined,
                undefined,
                searchText
              )
            }}
            locationBias={
              selectedLocation?.lat && selectedLocation?.long
                ? { lat: selectedLocation.lat, long: selectedLocation.long }
                : undefined
            }
            normalized
          />
        </div>
        <div className='attribution'>
          <span>{t('address-search-screen.powered-by')}</span>
          <Image
            src='icons/google_on_white_hdpi.png'
            alt={t('address-search-screen.google')}
          />
        </div>
      </div>
      {loading && <LoadingSpinnerOverlay withSubtleBorderRadius />}
    </div>
  )
}

export default AddressSearchScreen
